import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Brush01, CheckCircle, ChevronDown, ChevronUp, Palette, Trash01 } from '@opendate-app/setlist';
import { DEFAULTS } from '../../../WebOrderForm/utils/applyWidgetSettings';

const POPOVER_WIDTH = 320;
const POPOVER_OFFSET = 6;
const CARD_HEIGHT = 56;
const MAX_VISIBLE_CARDS = 5;

const computePosition = (anchorRect) => {
  if (!anchorRect) return { top: 0, left: 0, width: POPOVER_WIDTH };

  const viewportWidth = window.innerWidth;
  const width = Math.max(POPOVER_WIDTH, anchorRect.width);

  let top = anchorRect.bottom + POPOVER_OFFSET;
  let left = anchorRect.left;

  if (left + width > viewportWidth - 8) {
    left = Math.max(8, viewportWidth - width - 8);
  }

  return { top, left, width };
};

const customSubtitle = (template) => {
  const font   = template.font_family       || DEFAULTS.font_family;
  const accent = template.tab_active_color  || DEFAULTS.tab_active_color;
  const bg     = template.background_color  || DEFAULTS.background_color;
  return `${font.split(',')[0]} · ${accent} · ${bg}`;
};

const ThemePicker = ({
  widgetConfig,
  widgetTemplates = [],
  isEdited,
  onApply,
  onDelete
}) => {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: POPOVER_WIDTH });

  const selectedId = widgetConfig && widgetConfig.widget_template_id;
  const selected = widgetTemplates.find((t) => t.id === selectedId) || null;
  const currentName = selected ? selected.name : 'Opendate Default';

  const openPopover = () => {
    if (!triggerRef.current) return;
    setPosition(computePosition(triggerRef.current.getBoundingClientRect()));
    setOpen(true);
  };

  const closePopover = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closePopover();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleApply = (templateId) => {
    closePopover();
    onApply(templateId);
  };

  const handleDelete = (e, templateId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this theme? This can't be undone.")) return;
    onDelete(templateId);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`cwb-theme-trigger ${open ? 'open' : ''}`}
        onClick={open ? closePopover : openPopover}
        aria-expanded={open}>
        <div className="cwb-theme-trigger-body">
          <div className="cwb-theme-trigger-label">THEME</div>
          <div className="cwb-theme-trigger-name">
            {currentName}
            {isEdited && <span className="cwb-theme-trigger-edited"> · Edited</span>}
          </div>
        </div>
        {open
          ? <ChevronUp width={16} height={16} aria-hidden="true" className="cwb-theme-trigger-chevron" />
          : <ChevronDown width={16} height={16} aria-hidden="true" className="cwb-theme-trigger-chevron" />}
      </button>

      {open && ReactDOM.createPortal(
        <>
          <div className="cwb-theme-overlay" onClick={closePopover} />
          <div
            className="cwb-theme-popover"
            style={{ top: position.top, left: position.left, width: position.width }}
            onClick={(e) => e.stopPropagation()}>
            <div
              className="cwb-theme-card-list"
              style={{ maxHeight: CARD_HEIGHT * MAX_VISIBLE_CARDS + 8 }}>
              <ThemeCard
                icon={Palette}
                name="Opendate Default"
                subtitle="Default Opendate styling"
                selected={!selectedId}
                onClick={() => handleApply(null)}
              />
              {widgetTemplates.map((t) => (
                <ThemeCard
                  key={t.id}
                  icon={Brush01}
                  name={t.name}
                  subtitle={customSubtitle(t)}
                  selected={selectedId === t.id}
                  onClick={() => handleApply(t.id)}
                  onDelete={(e) => handleDelete(e, t.id)}
                />
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

const ThemeCard = ({ icon: Icon, name, subtitle, selected, onClick, onDelete }) => (
  <button
    type="button"
    className={`cwb-theme-card ${selected ? 'selected' : ''}`}
    onClick={onClick}>
    <div className="cwb-theme-card-icon">
      <Icon width={16} height={16} aria-hidden="true" />
    </div>
    <div className="cwb-theme-card-body">
      <div className="cwb-theme-card-name">{name}</div>
      <div className="cwb-theme-card-subtitle">{subtitle}</div>
    </div>
    {selected && (
      <CheckCircle width={16} height={16} aria-hidden="true" className="cwb-theme-card-check" />
    )}
    {onDelete && (
      <button
        type="button"
        className="cwb-theme-card-delete"
        title="Delete theme"
        aria-label="Delete theme"
        onClick={onDelete}>
        <Trash01 width={14} height={14} aria-hidden="true" />
      </button>
    )}
  </button>
);

export default ThemePicker;
